/**
 * Admin › Configurações › **Regras governadas** (W6-SETTINGS-CONSOLE, D400; ADR-0060) — the pure view model behind
 * `GovernedSettingsScreen`.
 *
 * The screen renders three things the server sends and one thing it must compute:
 *  - the **typed editor** for each registered key ({@link settingEditorFor}): a governed value is never a free-text
 *    blob, so each key gets the control its own validator accepts (a choice, a token list, a bounded number). A key
 *    the console does not know yet still gets an editor — a JSON one — rather than disappearing from the console;
 *  - the **diff against the version in force** ({@link settingValueDiff}): what a publish would change, field by
 *    field, so a checker reads a decision instead of two opaque blobs;
 *  - the **checker queue** and the timeline, whose tones come from the server's own lifecycle
 *    ({@link lifecycleTone}, {@link versionTone});
 *  - the **schedule**, typed in the market's wall clock and carried in UTC ({@link scheduleToUtc}, reusing the D358
 *    `market-time` conversion), because a bucket's instant belongs to its market, not to the operator's browser.
 *
 * Nothing here decides governance: the four-eyes rule, the base, the collision with a scheduled version and every
 * refusal are the server's (`/v1/config/settings/*` over the D393 kernel). This module never hides an action from an
 * operator either — the server decides, and the screen surfaces the refusal.
 *
 * Neutral: keys, tokens and values are data; no country, currency or locale literal.
 */
import { type GovernedSettingBucketView, type GovernedSettingDefinitionView, type GovernedSettingEffectiveQuery, type GovernedSettingEffectiveResponse, type SettingChangeView } from '@vello/api-client';
import type { OperatorScope } from './shell.js';
import { isValidTimeZone, zonedLocalToUtc, utcToZonedLocal } from './market-time.js';

// ---- the typed editor per key --------------------------------------------------------------------

/** A single-choice value (`dispatch.mode`). */
export interface ChoiceEditor {
  readonly kind: 'choice';
  readonly options: readonly string[];
}
/** A set of lower-case tokens (`payment.allowedMethods`, `partner.deskRideCategories`). */
export interface TokenListEditor {
  readonly kind: 'tokens';
  /** Tokens the pilot uses today, offered as toggles; the operator may still add another token. */
  readonly suggested: readonly string[];
  readonly allowEmpty: boolean;
}
/** A bounded integer plus the ride origins it applies to (`dispatch.searchTimeout`). */
export interface DurationEditor {
  readonly kind: 'duration';
  readonly minSeconds: number;
  readonly maxSeconds: number;
  readonly suggestedOrigins: readonly string[];
}
/**
 * W7-GOVERNED-OTP-KEYS (D410): `phone.callingCode` — the E.164 country calling code whose phones belong to the bucket's
 * country (and so whether OTP serves them). Digits only: no "+", no leading zero, at most `maxDigits`.
 */
export interface CallingCodeEditor {
  readonly kind: 'callingCode';
  readonly maxDigits: number;
}
/** One bounded integer of the `identity.otpAbuse` form (`path` is `dimension.field`). */
export interface OtpAbuseField {
  readonly path: string;
  readonly min: number;
  readonly max: number;
}
/**
 * D410: `identity.otpAbuse` — the D381/D383 thresholds as a form (never a JSON blob): a bounded number per field, the
 * phone's "challenge on the last token" switch, and the kind of human challenge.
 */
export interface OtpAbuseEditor {
  readonly kind: 'otpAbuse';
  readonly fields: readonly OtpAbuseField[];
  readonly challengeKinds: readonly string[];
}
/**
 * W8 (D438, finding C-18): `finance.refundFourEyesThreshold` — an amount in a currency, `{ amountMinor, currency }`, typed
 * through the design-system money input in the currency of the bucket's country (never raw JSON, never minor units).
 */
export interface MoneyEditor {
  readonly kind: 'money';
}
/**
 * D616 round 2: `geo.serviceAreaShrinkConfirmPercent` — one bounded integer percentage, stored as `{ confirmPercent }` (the share of the
 * area in force by which an approved service-area proposal may shrink before the approver must write a note). A number input, never JSON.
 */
export interface PercentEditor {
  readonly kind: 'percent';
  /** The member of the stored object that holds the number. */
  readonly field: string;
  readonly min: number;
  readonly max: number;
}
/**
 * D631 ("Atendimento e IA", doc 25 section 11; D639 adds `central.autonomy`): the keys of the Central's assistant and AI each get a FORM, never raw JSON -
 * `central.assistant` (name, languages, one disclosure line per language), `central.autoRefundCaps` (two amounts in the country's currency),
 * `central.orderPaymentMethods` (per service, which of the country's methods the Central may offer), `ai.models` (a model and a fallback per
 * purpose), `ai.speechEngine` (OpenRouter, or a self-hosted endpoint by credential name) and `ai.monthlyBudget` (an amount and its alert
 * thresholds). Their logic is `central-ai-settings.ts`; the controls are `CentralAiValueEditor`.
 */
export interface CentralAiEditor {
  readonly kind: 'assistant' | 'refundCaps' | 'orderPayments' | 'aiModels' | 'speechEngine' | 'aiBudget' | 'autonomy';
}
/** Any other registered key: the raw JSON value, validated by the server's `preview`. */
export interface JsonEditor {
  readonly kind: 'json';
}

export type SettingEditor = ChoiceEditor | TokenListEditor | DurationEditor | CallingCodeEditor | OtpAbuseEditor | MoneyEditor | PercentEditor | CentralAiEditor | JsonEditor;

/** The bounds of `dispatch.searchTimeout` (owner decision D391 #5; the server refuses anything outside them). */
export const SEARCH_TIMEOUT_BOUNDS = { minSeconds: 60, maxSeconds: 86_400 } as const;

/**
 * The fields and bounds of `identity.otpAbuse` — a mirror of `@vello/identity` `OTP_ABUSE_BOUNDS` (the console does not
 * import the identity kernel). The bounds are a HINT on the inputs: the server's typed validator and the database floor
 * (m0131) decide, and `preview` reports a value outside them before a reason is typed.
 */
export const OTP_ABUSE_FIELDS: readonly OtpAbuseField[] = [
  { path: 'phone.capacity', min: 1, max: 1_000 },
  { path: 'phone.refillSeconds', min: 1, max: 86_400 },
  { path: 'phone.dailyCap', min: 1, max: 1_000 },
  { path: 'device.capacity', min: 1, max: 1_000 },
  { path: 'device.refillSeconds', min: 1, max: 86_400 },
  { path: 'ip.capacity', min: 1, max: 100_000 },
  { path: 'ip.refillSeconds', min: 1, max: 86_400 },
  { path: 'countryBreaker.perHour', min: 1, max: 10_000_000 },
  { path: 'challenge.difficultyBits', min: 8, max: 24 },
  { path: 'challenge.ttlSeconds', min: 30, max: 900 },
];

/** A typed calling code as the kernel stores it: trimmed digits, 1–4 of them, no "+", no leading zero. */
export function callingCodeDraft(raw: string): { readonly ok: true; readonly value: string } | { readonly ok: false } {
  const v = raw.trim();
  return /^[1-9][0-9]{0,3}$/.test(v) ? { ok: true, value: v } : { ok: false };
}

const asRecord = (v: unknown): Record<string, unknown> | undefined => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined);

/** The value of one `dimension.field` of an OTP draft, or undefined. */
export function otpAbuseFieldValue(value: unknown, path: string): unknown {
  const [dimension, field] = path.split('.') as [string, string];
  return asRecord(asRecord(value)?.[dimension])?.[field];
}

/** The draft with one `dimension.field` set, every other field kept (a draft with no value yet starts empty). */
export function withOtpAbuseField(value: unknown, path: string, next: unknown): Record<string, unknown> {
  const [dimension, field] = path.split('.') as [string, string];
  const root = { ...(asRecord(value) ?? {}) };
  root[dimension] = { ...(asRecord(root[dimension]) ?? {}), [field]: next };
  return root;
}

/** The editor a key gets. Unknown keys fall back to JSON, so a newly registered key is editable on day one. */
export function settingEditorFor(key: string): SettingEditor {
  switch (key) {
    case 'dispatch.mode':
      return { kind: 'choice', options: ['ASSISTED', 'AUTOMATIC'] };
    case 'payment.allowedMethods':
      return { kind: 'tokens', suggested: ['cash', 'card', 'account'], allowEmpty: false };
    case 'partner.deskRideCategories':
      return { kind: 'tokens', suggested: ['go', 'moto', 'lux'], allowEmpty: true };
    case 'dispatch.searchTimeout':
      return { kind: 'duration', ...SEARCH_TIMEOUT_BOUNDS, suggestedOrigins: ['partner_desk'] };
    case 'phone.callingCode':
      return { kind: 'callingCode', maxDigits: 4 };
    case 'identity.otpAbuse':
      return { kind: 'otpAbuse', fields: OTP_ABUSE_FIELDS, challengeKinds: ['proof_of_work'] };
    case 'finance.refundFourEyesThreshold':
      return { kind: 'money' };
    case 'geo.serviceAreaShrinkConfirmPercent':
      return { kind: 'percent', field: 'confirmPercent', min: 1, max: 100 };
    case 'central.assistant':
      return { kind: 'assistant' };
    case 'central.autoRefundCaps':
      return { kind: 'refundCaps' };
    case 'central.orderPaymentMethods':
      return { kind: 'orderPayments' };
    case 'ai.models':
      return { kind: 'aiModels' };
    case 'ai.speechEngine':
      return { kind: 'speechEngine' };
    case 'ai.monthlyBudget':
      return { kind: 'aiBudget' };
    case 'central.autonomy':
      return { kind: 'autonomy' };
    default:
      return { kind: 'json' };
  }
}

/** The value an editor starts from when a bucket has one in force, else the key's default, else the editor's zero. */
export function initialEditorValue(def: GovernedSettingDefinitionView, bucket: GovernedSettingBucketView | undefined): unknown {
  // An UNVERIFIED version carries a placeholder no reader accepts (review W6G-04): never start a draft from it.
  if (bucket?.current !== undefined && bucket.current.origin !== 'UNVERIFIED') return bucket.current.value;
  if (def.defaultValue !== undefined) return def.defaultValue;
  const editor = settingEditorFor(def.key);
  if (editor.kind === 'choice') return editor.options[0];
  if (editor.kind === 'tokens') return [];
  if (editor.kind === 'duration') return { timeoutSeconds: editor.minSeconds, rideOrigins: [...editor.suggestedOrigins] };
  // D410: a country with no calling code or no thresholds yet starts EMPTY — never a guessed code or a guessed threshold.
  if (editor.kind === 'callingCode') return '';
  // D631: a first version of an "Atendimento e IA" key starts with NO draft: its form reports a value only once it is complete, so the
  // primary action stays off until then (never a guessed amount, model or payment option).
  if (editor.kind === 'refundCaps' || editor.kind === 'orderPayments' || editor.kind === 'aiModels' || editor.kind === 'speechEngine' || editor.kind === 'aiBudget' || editor.kind === 'assistant') return undefined;
  // D639: the autonomy form is always complete - a first version starts from the conservative default (Vello on, everything in shadow).
  if (editor.kind === 'autonomy') return def.defaultValue ?? undefined;
  return null;
}

// ---- the money editor (D438, C-18) ---------------------------------------------------------------

const ISO4217 = /^[A-Z]{3}$/;

/** A stored money value `{ amountMinor, currency }` (a non-negative safe integer, an ISO 4217 code), or undefined. */
export function moneySettingOf(value: unknown): { readonly amountMinor: bigint; readonly currency: string } | undefined {
  const o = asRecord(value);
  if (o === undefined) return undefined;
  const amount = o.amountMinor;
  const currency = o.currency;
  if (typeof currency !== 'string' || !ISO4217.test(currency)) return undefined;
  if (typeof amount === 'number' && Number.isSafeInteger(amount) && amount >= 0) return { amountMinor: BigInt(amount), currency };
  // Tolerate a decimal string (a minor-unit wire habit); the value written back is always a number.
  if (typeof amount === 'string' && /^\d{1,15}$/.test(amount)) return { amountMinor: BigInt(amount), currency };
  return undefined;
}

/**
 * The value the money editor writes: `{ amountMinor, currency }` with `amountMinor` a safe-integer NUMBER (the kernel's
 * validator — `validRefundThresholdValue` — refuses a string), or undefined while the amount is missing or too large.
 */
export function moneySettingValue(amountMinor: bigint | undefined, currency: string): { readonly amountMinor: number; readonly currency: string } | undefined {
  if (amountMinor === undefined || amountMinor < 0n || amountMinor > BigInt(Number.MAX_SAFE_INTEGER) || !ISO4217.test(currency)) return undefined;
  return { amountMinor: Number(amountMinor), currency };
}

/**
 * The currency the money editor types in: the one the draft or the version in force already carries (a threshold's
 * currency is part of its value), else the country pack's (the bucket's country), else undefined — then the screen
 * says it cannot tell and keeps the raw editor, instead of guessing a currency.
 */
export function moneySettingCurrency(candidates: readonly unknown[], packCurrency?: string): string | undefined {
  for (const c of candidates) {
    const m = moneySettingOf(c);
    if (m !== undefined) return m.currency;
  }
  return packCurrency !== undefined && ISO4217.test(packCurrency) ? packCurrency : undefined;
}

/**
 * The country pack whose currency a country bucket's money value is typed in: the APPROVED pack in force at `now`
 * (the newest version whose window holds it). A DRAFT or RETIRED pack never supplies a currency.
 */
export function countryPackCurrencyAt(packs: readonly { readonly status: string; readonly version: number; readonly effectiveFrom: string; readonly effectiveTo?: string; readonly currency: string }[], now: string): string | undefined {
  const at = Date.parse(now);
  const live = packs
    .filter((p) => p.status === 'APPROVED' && Date.parse(p.effectiveFrom) <= at && (p.effectiveTo === undefined || at < Date.parse(p.effectiveTo)))
    .sort((a, b) => b.version - a.version);
  const currency = live[0]?.currency;
  return currency !== undefined && ISO4217.test(currency) ? currency : undefined;
}

// ---- the diff against the version in force -------------------------------------------------------

export type SettingDiffKind = 'added' | 'removed' | 'changed' | 'unchanged';

export interface SettingDiffRow {
  /** The field path inside the value (`''` for a scalar value, `timeoutSeconds`, `rideOrigins[0]`, …). */
  readonly path: string;
  readonly kind: SettingDiffKind;
  /** The rendered previous / next value; `undefined` when the field does not exist on that side. */
  readonly from?: string;
  readonly to?: string;
}

const render = (v: unknown): string => (typeof v === 'string' ? v : JSON.stringify(v) ?? 'null');
const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function walk(path: string, from: unknown, to: unknown, out: SettingDiffRow[]): void {
  if (isPlainObject(from) && isPlainObject(to)) {
    for (const key of [...new Set([...Object.keys(from), ...Object.keys(to)])].sort()) {
      walk(path === '' ? key : `${path}.${key}`, from[key], to[key], out);
    }
    return;
  }
  if (Array.isArray(from) && Array.isArray(to)) {
    for (let i = 0; i < Math.max(from.length, to.length); i++) walk(`${path}[${i}]`, from[i], to[i], out);
    return;
  }
  if (from === undefined && to === undefined) return;
  if (from === undefined) out.push({ path, kind: 'added', to: render(to) });
  else if (to === undefined) out.push({ path, kind: 'removed', from: render(from) });
  else if (JSON.stringify(from) === JSON.stringify(to)) out.push({ path, kind: 'unchanged', from: render(from), to: render(to) });
  else out.push({ path, kind: 'changed', from: render(from), to: render(to) });
}

/**
 * A field-level diff of a candidate value against the one in force. A scalar value diffs as one row with an empty
 * path. The rows keep `unchanged` fields so a reviewer sees the whole value, not only what moved.
 */
export function settingValueDiff(from: unknown, to: unknown): readonly SettingDiffRow[] {
  const rows: SettingDiffRow[] = [];
  walk('', from, to, rows);
  return rows;
}

/** Whether a diff changes anything at all (what the screen uses to keep the primary action disabled). */
export const settingDiffChanges = (rows: readonly SettingDiffRow[]): boolean => rows.some((r) => r.kind !== 'unchanged');

// ---- tones ---------------------------------------------------------------------------------------

export type SettingTone = 'neutral' | 'success' | 'warning' | 'danger';

/** The tone of a change's lifecycle status (the server derives the status; this only paints it). */
export function lifecycleTone(lifecycle: SettingChangeView['lifecycle']): SettingTone {
  if (lifecycle === 'ACTIVE') return 'success';
  if (lifecycle === 'PENDING_REVIEW' || lifecycle === 'SCHEDULED') return 'warning';
  if (lifecycle === 'REJECTED' || lifecycle === 'EXPIRED') return 'danger';
  return 'neutral';
}

/** The tone of a published version's place in its bucket's timeline. */
export function versionTone(status: 'SCHEDULED' | 'ACTIVE' | 'SUPERSEDED' | 'ENDED'): SettingTone {
  return status === 'ACTIVE' ? 'success' : status === 'SCHEDULED' ? 'warning' : 'neutral';
}

/**
 * The tone of a version's provenance. A LEGACY row ("sem revisão (seed)") and an UNVERIFIED row are not the same
 * thing: the first governs on purpose (owner decision D391 #5), the second is a damaged row that fails its bucket
 * closed and needs an operator.
 */
export function originTone(origin: string): SettingTone {
  if (origin === 'GOVERNED' || origin === 'ATTESTED') return 'success';
  if (origin === 'UNVERIFIED') return 'danger';
  return 'warning';
}

// ---- scheduling in the market's time zone --------------------------------------------------------

export type ScheduleIssue = 'INVALID_LOCAL' | 'NOT_IN_FUTURE' | 'NO_TIMEZONE';

export type ScheduleResult =
  | { readonly ok: true; readonly instant: string }
  | { readonly ok: false; readonly issue: ScheduleIssue };

/**
 * A `datetime-local` value typed in the bucket's market time zone, as the UTC instant the API takes. A bucket with no
 * known time zone (platform, country, or a market the registry did not answer for) cannot be scheduled from a
 * wall-clock field — the operator would be typing in an unstated zone — so it is refused rather than silently read as
 * the browser's local time (`NO_TIMEZONE`). W6S-05: the conversion is the console's, and this is where it lives.
 */
export function scheduleToUtc(local: string, timeZone: string | undefined, now: string): ScheduleResult {
  if (!isValidTimeZone(timeZone)) return { ok: false, issue: 'NO_TIMEZONE' };
  const instant = zonedLocalToUtc(local, timeZone);
  if (instant === undefined) return { ok: false, issue: 'INVALID_LOCAL' };
  if (Date.parse(instant) <= Date.parse(now)) return { ok: false, issue: 'NOT_IN_FUTURE' };
  return { ok: true, instant };
}

/** A stored UTC instant as the `datetime-local` value of the bucket's market wall clock (for prefilling the field). */
export function scheduleFromUtc(instant: string, timeZone: string | undefined): string {
  return isValidTimeZone(timeZone) ? (utcToZonedLocal(instant, timeZone) ?? '') : '';
}

// ---- what the screen must warn about -------------------------------------------------------------

/**
 * Review W6S-01 / W6G-02. A bucket can hold SEVERAL versions SCHEDULED for future instants. A change decided for an
 * earlier instant takes a higher version, so every waiting version it lands on or before never governs — the console
 * warns BEFORE a reason is typed, and the API refuses the decision (`SCHEDULED_VERSION_COLLISION`) until the operator
 * names EVERY version it cancels. Returns those versions (ascending), or `[]` when the decision lands strictly after all
 * of them. An older server that only sends `scheduled` is read as a one-element list.
 */
export function scheduledCollisions(bucket: GovernedSettingBucketView | undefined, effectiveFrom: string | undefined, now: string): readonly number[] {
  const waiting = bucket?.scheduledVersions ?? (bucket?.scheduled !== undefined ? [bucket.scheduled] : []);
  const at = Date.parse(effectiveFrom ?? now);
  return waiting.filter((w) => at <= Date.parse(w.effectiveFrom)).map((w) => w.version).sort((a, b) => a - b);
}

/**
 * Whether "Reverter para vN" can apply to a version at all: only a version OLDER than the one in force can be restored
 * (the server refuses the current one and every later one — `ROLLBACK_TARGET_IS_CURRENT` / `_NOT_EARLIER`). A scheduled
 * version is later by construction, so it never offers a revert (review W6G-08).
 */
export function canRevertTo(bucket: GovernedSettingBucketView | undefined, version: { version: number }): boolean {
  const current = bucket?.current?.version;
  return current !== undefined && version.version < current;
}

/**
 * The version a one-step rollback may restore — the one immediately preceding the effective version, if any — which
 * applies at once and is reviewed afterwards. Undefined for a key whose every rollback waits for a different operator
 * (`rollbackRequiresReview`, the two OTP keys — W7O-03, D414): there the console PROPOSES every revert.
 */
export function oneStepRollbackTarget(
  bucket: GovernedSettingBucketView | undefined,
  versions: readonly { version: number; scope: string; scopeRef: string }[],
  setting?: { readonly rollbackRequiresReview?: true },
): number | undefined {
  if (setting?.rollbackRequiresReview === true) return undefined;
  const current = bucket?.current?.version;
  if (bucket === undefined || current === undefined) return undefined;
  const earlier = versions
    .filter((v) => v.scope === bucket.scope && v.scopeRef === bucket.scopeRef && v.version < current)
    .map((v) => v.version);
  return earlier.length === 0 ? undefined : Math.max(...earlier);
}

/** A bucket's label for the scope selector (`Plataforma`, `BR`, `br:paracatu`) — the ref is data, never translated. */
export const bucketLabel = (bucket: { scope: string; scopeRef: string }, platformLabel: string): string =>
  bucket.scope === 'platform' ? platformLabel : bucket.scopeRef;

// ---- the state level (STATE-SCOPE F1, ADR-0124) --------------------------------------------------

/**
 * The place a bucket stands for, as the `effective` read takes it: a market (its state comes from the registry), a state (its country is the code's
 * prefix), a country, or - for the platform bucket - nothing.
 */
export function effectiveQueryOf(bucket: { readonly scope: string; readonly scopeRef: string }): GovernedSettingEffectiveQuery {
  if (bucket.scope === 'market') return { market: bucket.scopeRef };
  if (bucket.scope === 'region') return { region: bucket.scopeRef };
  if (bucket.scope === 'country') return { country: bucket.scopeRef };
  return {};
}

/**
 * Whether the screen shows the "where the value comes from" chain for a bucket: only for a key that can be set at a state (those are the keys whose
 * value is folded through the new level) and only below the platform, where there is something to inherit from.
 */
export function showsInheritance(def: Pick<GovernedSettingDefinitionView, 'allowedScopes'>, bucket: { readonly scope: string } | undefined): boolean {
  return bucket !== undefined && bucket.scope !== 'platform' && def.allowedScopes.includes('region');
}

/** One row of the chain a value is inherited through: the layer, what it holds, and whether it is the one that decides here. */
export interface InheritanceRow {
  readonly scope: GovernedSettingEffectiveResponse['chain'][number]['scope'];
  readonly scopeRef: string;
  readonly current?: NonNullable<GovernedSettingEffectiveResponse['chain'][number]['current']>;
  /** The layer whose version the fold picked (`resolution.scope` + `scopeRef`). */
  readonly decides: boolean;
  /** The layer holds a version that no reader can verify: it fails closed. */
  readonly failClosed: boolean;
}

/**
 * The chain from the server's answer, most specific first, each row marked when it is the layer that decides. At most ONE row decides (the server's fold
 * names a single winner); when nothing is set at any layer none does. The order is the server's - this only paints it.
 */
export function inheritanceRows(effective: GovernedSettingEffectiveResponse): readonly InheritanceRow[] {
  const winner = effective.resolution.scope;
  const winnerRef = effective.resolution.scopeRef ?? '';
  return effective.chain.map((layer) => ({
    scope: layer.scope,
    scopeRef: layer.scopeRef,
    ...(layer.current !== undefined ? { current: layer.current } : {}),
    decides: winner !== undefined && layer.scope === winner && layer.scopeRef === winnerRef,
    failClosed: layer.failClosed === true,
  }));
}

/**
 * The bucket the screen opens on when the top scope selector is on a STATE (no city under it): that state's bucket, when the screen lists it. The operator
 * can still pick another place in the selector; the choice is reset when the top selector moves.
 */
export function followedBucketId(scope: OperatorScope, buckets: readonly { readonly scope: string; readonly scopeRef: string }[]): string | undefined {
  if (scope.region === undefined || scope.city !== undefined) return undefined;
  const code = scope.region.toUpperCase();
  const hit = buckets.find((b) => b.scope === 'region' && b.scopeRef.toUpperCase() === code);
  return hit === undefined ? undefined : `${hit.scope}|${hit.scopeRef}`;
}

/**
 * The buckets the screen lists: the ones that hold a version or a change, plus - for a key that can be set at a state - an EMPTY bucket for the state the top
 * selector is on, so its first value can be proposed from there. Sorted from the broadest place to the narrowest only when something was added.
 */
export function withStateBucket(
  def: Pick<GovernedSettingDefinitionView, 'allowedScopes' | 'defaultValue'>,
  held: readonly GovernedSettingBucketView[],
  scope: OperatorScope,
  makeEmpty: (regionCode: string, defaultValue: unknown) => GovernedSettingBucketView,
): readonly GovernedSettingBucketView[] {
  if (scope.region === undefined || scope.city !== undefined || !def.allowedScopes.includes('region')) return held;
  const code = scope.region.toUpperCase();
  if (held.some((b) => b.scope === 'region' && b.scopeRef.toUpperCase() === code)) return held;
  return [...held, makeEmpty(code, def.defaultValue)].sort((a, b) => `${scopeRank(a.scope)}${a.scopeRef}`.localeCompare(`${scopeRank(b.scope)}${b.scopeRef}`));
}

/** The order of the places in the selector: platform, country, state, market. */
export const scopeRank = (scope: string): string => (scope === 'platform' ? '0' : scope === 'country' ? '1' : scope === 'region' ? '2' : '3');
